import { ShieldCheck, Cpu, Database, Anchor, Zap, Satellite, Info } from "lucide-react";
import { Reveal, SectionHead } from "./primitives";

const COMPONENTS = [
  {
    tid: "stack-validator-node",
    icon: ShieldCheck,
    title: "Validator Node",
    purpose: "Secures the underlying Flare network by validating transactions and participating in the proof-of-stake consensus mechanism.",
    secondLabel: "Requirement",
    second: "Must maintain required uptime and meet self-bond staking minimums, such as holding a self-bond of native FLR tokens.",
  },
  {
    tid: "stack-flare-system-client",
    icon: Cpu,
    title: "Flare System Client",
    purpose: "Manages all direct interactions with the Flare Systems Protocol and FTSO smart contracts.",
    secondLabel: "Functions",
    second: "Handles voter registration, round data collection, submitting calculated consensus values across protocols, and executing system-level tasks.",
  },
  {
    tid: "stack-cchain-indexer",
    icon: Database,
    title: "C-Chain Indexer",
    purpose: "Tracks and captures FSP-related blockchain transactions and contract events in real time.",
    secondLabel: "Functions",
    second: "Supplies historical and live transactional context used by the local client modules for data calculations and action triggers.",
  },
  {
    tid: "stack-ftso-client",
    icon: Anchor,
    title: "FTSO Client — Anchor Feeds",
    purpose: "Supports standard FTSOv2 anchor feed submissions, updating every 90 seconds.",
    secondLabel: "Functions",
    second: "Processes protocol votes and computes median data structures before passing them to the Flare System Client for final submission.",
  },
  {
    tid: "stack-fast-updates-client",
    icon: Zap,
    title: "Fast Updates Client — Block-Latency Feeds",
    purpose: "Handles high-frequency, block-latency feeds for FTSOv2 that update with every new network block.",
    secondLabel: "Functions",
    second: "Rapidly collects and pushes low-latency pricing and state updates straight to the Fast Updates smart contracts.",
  },
  {
    tid: "stack-feed-value-provider",
    icon: Satellite,
    title: "Feed Value Provider",
    purpose: "Connects off-chain infrastructure to the external world to gather raw market data.",
    secondLabel: "Functions",
    second: "Sources real-time pricing and time-series information from external APIs and crypto exchanges to supply current feed values to the internal pipeline.",
  },
];

export default function Stack() {
  return (
    <section className="section" id="stack" data-testid="stack-section">
      <div className="wrap">
        <SectionHead
          index="03"
          label="Provider stack"
          title="Inside the stack."
          right={<span className="infra-count">6 components</span>}
        />
        <div className="stack-rows">
          {COMPONENTS.map((c, i) => (
            <Reveal key={c.tid} delay={Math.min(i * 0.05, 0.2)}>
              <div className="stack-row" data-testid={c.tid}>
                <div className="stack-meta">
                  <span className="stack-idx">{String(i + 1).padStart(2, "0")}</span>
                  <span className="card-icon">
                    <c.icon size={20} strokeWidth={1.75} />
                  </span>
                </div>
                <div>
                  <h3>{c.title}</h3>
                  <div className="stack-field">
                    <span className="stack-label">Purpose</span>
                    <p>{c.purpose}</p>
                  </div>
                </div>
                <div className="stack-field stack-second">
                  <span className="stack-label">{c.secondLabel}</span>
                  <p>{c.second}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="stack-note" data-testid="stack-fdc-note">
            <Info size={16} strokeWidth={1.75} />
            <p>
              Depending on full reward operational requirements, a Flare Data Connector (FDC) client also runs
              alongside these components to manage attestation voting data.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
